/**
 * 계측 사진 컴포넌트
 * 점검 사진(check) / 결과 사진(result) 2칸 가로 레이아웃
 * 카메라/갤러리 선택, 프리뷰 모달, 삭제 기능
 *
 * 점검 탭(ChecklistTab)의 ImageUploadSlot과 동일한 UI:
 * - 빈 슬롯: PhotoCaptureSlot (4:3, 카메라 + 갤러리 버튼)
 * - 이미지 있는 슬롯: 이미지(4:3) + 삭제 버튼 + 갤러리에서 교체 버튼
 */

import { useCallback, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { X, ImagePlus, Loader2 } from "lucide-react";
import { createLogger } from "@ict/shared";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { InAppCamera } from "@/components/camera/InAppCamera";
import { base64ToFile, generatePhotoFileName } from "@/lib/camera-utils";
import { PhotoCaptureSlot } from "@/components/inspection/shared/PhotoCaptureSlot";
import { ImagePreviewModal } from "@/components/inspection/shared/ImagePreviewModal";

const logger = createLogger({ module: "MeasurePhoto" });

interface MeasurePhotoProps {
  checkPhotoUrl?: string;
  resultPhotoUrl?: string;
  onPhotoCapture: (type: "check" | "result", file: File) => void;
  onPhotoDelete: (type: "check" | "result") => void;
  uploadingSlot?: "check" | "result" | null;
  disabled?: boolean;
}

type SlotType = "check" | "result";

const SLOT_LABELS: Record<SlotType, string> = {
  check: "점검 사진",
  result: "결과 사진",
};

/** 업로드 중 슬롯 (사진 URL 없는 상태) */
function UploadingSlot({ label }: { label: string }) {
  return (
    <div className="aspect-[4/3] rounded-xl bg-gray-100 flex flex-col items-center justify-center gap-2">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      <div className="bg-white rounded-full p-2.5 shadow-md">
        <Loader2 className="w-6 h-6 animate-spin text-[#3182F6]" />
      </div>
      <span className="text-xs text-gray-400">업로드 중...</span>
    </div>
  );
}

/** 사진 슬롯 (이미지 표시 + 삭제 + 갤러리 교체) */
function PhotoSlot({
  url,
  label,
  onDelete,
  onPreview,
  onGalleryReplace,
  uploading,
  disabled,
}: {
  url: string;
  label: string;
  onDelete: () => void;
  onPreview: () => void;
  onGalleryReplace: (file: File) => void;
  uploading?: boolean;
  disabled?: boolean;
}) {
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // 갤러리에서 교체 클릭
  const handleGalleryClick = useCallback(() => {
    galleryInputRef.current?.click();
  }, []);

  // 갤러리 파일 선택 핸들러
  const handleGalleryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        onGalleryReplace(file);
      }
      if (galleryInputRef.current) {
        galleryInputRef.current.value = "";
      }
    },
    [onGalleryReplace]
  );

  return (
    <div className="flex flex-col gap-2">
      {/* 이미지 영역 */}
      <div className="relative">
        {/* 이미지 (클릭 시 프리뷰) */}
        <button
          type="button"
          onClick={onPreview}
          className="w-full"
          aria-label={`${label} 미리보기`}
        >
          <img src={url} alt={label} className="w-full aspect-[4/3] object-cover rounded-xl" />
        </button>

        {/* 업로드 중 오버레이 */}
        {uploading && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded-xl">
            <div className="bg-white rounded-full p-2.5 shadow-md">
              <Loader2 className="w-6 h-6 animate-spin text-[#3182F6]" />
            </div>
          </div>
        )}

        {/* 삭제 버튼 */}
        {!disabled && !uploading && (
          <button
            type="button"
            onClick={onDelete}
            className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs active:bg-red-600 shadow-sm"
            aria-label={`${label} 삭제`}
          >
            <X className="w-3.5 h-3.5" strokeWidth={2.5} />
          </button>
        )}
      </div>

      {/* 갤러리에서 교체 버튼 */}
      {!disabled && !uploading && (
        <button
          type="button"
          onClick={handleGalleryClick}
          className="group relative w-full py-2.5 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/80 flex items-center justify-center gap-1.5 active:scale-[0.98] transition-all duration-150 disabled:opacity-50 overflow-hidden"
          aria-label={`${label} 갤러리에서 교체`}
        >
          {/* 액티브 시 배경 효과 */}
          <span className="absolute inset-0 bg-gradient-to-r from-blue-100/0 to-blue-100/50 opacity-0 group-active:opacity-100 transition-opacity" />
          <ImagePlus className="w-4 h-4 text-blue-500 relative z-10" />
          <span className="text-xs font-medium text-slate-600 relative z-10">갤러리에서 교체</span>
        </button>
      )}

      {/* Hidden file input (갤러리 교체용) */}
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        onChange={handleGalleryChange}
        className="hidden"
      />
    </div>
  );
}

export function MeasurePhoto({
  checkPhotoUrl,
  resultPhotoUrl,
  onPhotoCapture,
  onPhotoDelete,
  uploadingSlot,
  disabled,
}: MeasurePhotoProps) {
  const isNative = Capacitor.isNativePlatform();
  const [cameraOpen, setCameraOpen] = useState(false);
  /** 현재 카메라로 촬영 중인 슬롯 */
  const [activeSlot, setActiveSlot] = useState<SlotType | null>(null);
  /** 프리뷰 모달에 표시 중인 슬롯 */
  const [previewSlot, setPreviewSlot] = useState<SlotType | null>(null);
  /** 삭제 확인 대상 슬롯 */
  const [deleteTarget, setDeleteTarget] = useState<SlotType | null>(null);
  /** 재촬영 시 웹 환경용 file input */
  const retakeInputRef = useRef<HTMLInputElement>(null);

  // 카메라 촬영 완료 (InAppCamera → base64)
  const handleCameraCapture = useCallback(
    (base64: string) => {
      if (!activeSlot) return;
      const file = base64ToFile(base64, generatePhotoFileName());
      logger.debug("인앱 카메라 촬영 완료", { slot: activeSlot });
      onPhotoCapture(activeSlot, file);
      setCameraOpen(false);
      setActiveSlot(null);
    },
    [activeSlot, onPhotoCapture]
  );

  // 인앱 카메라 열기
  const openCamera = useCallback((slot: SlotType) => {
    setActiveSlot(slot);
    setCameraOpen(true);
  }, []);

  // 인앱 카메라 닫기
  const handleCameraClose = useCallback(() => {
    setCameraOpen(false);
    setActiveSlot(null);
  }, []);

  // 파일 선택 (카메라 폴백 또는 갤러리)
  const handleFileCapture = useCallback(
    (slot: SlotType, file: File) => {
      logger.debug("파일 선택", { slot, fileName: file.name });
      onPhotoCapture(slot, file);
    },
    [onPhotoCapture]
  );

  // 삭제 확인 요청
  const handleDeleteRequest = useCallback((slot: SlotType) => {
    setDeleteTarget(slot);
  }, []);

  // 삭제 확인
  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    logger.debug("사진 삭제", { slot: deleteTarget });
    onPhotoDelete(deleteTarget);
    setDeleteTarget(null);
  }, [deleteTarget, onPhotoDelete]);

  // 삭제 취소
  const handleDeleteCancel = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  // 프리뷰 열기/닫기
  const openPreview = useCallback((slot: SlotType) => {
    setPreviewSlot(slot);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewSlot(null);
  }, []);

  // 프리뷰에서 재촬영
  const handleRetake = useCallback(() => {
    if (!previewSlot) return;
    const slot = previewSlot;
    setPreviewSlot(null);
    if (isNative) {
      openCamera(slot);
    } else {
      // 웹: 파일 선택 다이얼로그
      setActiveSlot(slot);
      retakeInputRef.current?.click();
    }
  }, [previewSlot, isNative, openCamera]);

  // 슬롯 렌더링 헬퍼
  const renderSlot = (slot: SlotType, photoUrl?: string) => {
    const label = SLOT_LABELS[slot];
    const isUploading = uploadingSlot === slot;

    // 업로드 중 (사진 URL 없는 상태)
    if (isUploading && !photoUrl) {
      return <UploadingSlot key={slot} label={label} />;
    }

    // 사진이 있는 상태
    if (photoUrl) {
      return (
        <PhotoSlot
          key={slot}
          url={photoUrl}
          label={label}
          onDelete={() => handleDeleteRequest(slot)}
          onPreview={() => openPreview(slot)}
          onGalleryReplace={(file: File) => handleFileCapture(slot, file)}
          uploading={isUploading}
          disabled={disabled}
        />
      );
    }

    // 빈 상태
    return (
      <PhotoCaptureSlot
        key={slot}
        label={label}
        onFileSelect={(file: File) => handleFileCapture(slot, file)}
        onCameraOpen={isNative ? () => openCamera(slot) : undefined}
        disabled={disabled || !!uploadingSlot}
      />
    );
  };

  // 프리뷰 URL 결정
  const previewUrl =
    previewSlot === "check" ? checkPhotoUrl : previewSlot === "result" ? resultPhotoUrl : undefined;

  return (
    <>
      {/* 2칸 가로 레이아웃 */}
      <div className="grid grid-cols-2 gap-3">
        {renderSlot("check", checkPhotoUrl)}
        {renderSlot("result", resultPhotoUrl)}
      </div>

      {/* 인앱 카메라 (네이티브 환경에서만) */}
      {isNative && (
        <InAppCamera
          isOpen={cameraOpen}
          onCapture={handleCameraCapture}
          onClose={handleCameraClose}
          maxPhotos={1}
        />
      )}

      {/* 재촬영용 hidden file input (웹 환경) */}
      <input
        ref={retakeInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && activeSlot) {
            handleFileCapture(activeSlot, file);
            setActiveSlot(null);
          }
          if (retakeInputRef.current) {
            retakeInputRef.current.value = "";
          }
        }}
        className="hidden"
      />

      {/* 프리뷰 모달 */}
      {previewSlot && previewUrl && (
        <ImagePreviewModal
          isOpen={true}
          imageUrl={previewUrl}
          label={SLOT_LABELS[previewSlot]}
          onClose={closePreview}
          onRetake={handleRetake}
        />
      )}

      {/* 삭제 확인 다이얼로그 */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="사진 삭제"
        message={deleteTarget ? `${SLOT_LABELS[deleteTarget]}을 삭제하시겠습니까?` : ""}
        variant="destructive"
        confirmText="삭제"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
}
